import React, { useState, useEffect, useCallback } from 'react';
import { SearchInput } from '../../../../lib/iprs-react-library/src/SharedComponents/InputComponents/SearchInput';
import { getURLSearchParam } from '../../../../lib/iprs-react-library/src';
import OutputReportRow from './OutputReportRow';
import './OutputReports.css';

// Override: appending ?casereportallclients=true to the portal URL tells the
// backend to skip the per-client "portal delivery enabled" filter.
//
// This is safe to leave open to users: the backend always scopes results to the
// logged-in recipient, so a user only ever sees reports that are subscribed to
// them. The client flag the override bypasses is just a rollout gate so that
// clients who haven't asked to use the portal feature don't see their reports
// here yet — it is not a security boundary.
const includeAllClients = (getURLSearchParam('casereportallclients') ?? '').toLowerCase() === 'true';

const OutputReportList = ({ api }) => {
    const [reports, setReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [hideDownloaded, setHideDownloaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const searchProperties = ['PatientName', 'ClientName', 'CaseId', 'Description', 'Name'];

    const fetchReports = useCallback(() => {
        setLoading(true);
        setError(null);
        api.transact("GetOutputReportTransfers", { IncludeAllClients: includeAllClients })
            .then(r => r.apiResult)
            .then(data => {
                setReports(data || []);
                setFilteredReports(data || []);
            })
            .catch(err => {
                setError(err.message || 'Failed to load reports');
                setReports([]);
                setFilteredReports([]);
            })
            .finally(() => setLoading(false));
    }, [api]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleDownloaded = (id) => {
        const updateReport = (list) =>
            list.map(r =>
                r.PkOutputReportTransferId === id
                    ? { ...r, Downloaded: true, DownloadedTime: new Date().toISOString() }
                    : r
            );
        setReports(prev => updateReport(prev));
        setFilteredReports(prev => updateReport(prev));
    };

    if (loading) {
        return (
            <div className="container typography" style={{ padding: '20px' }}>
                <h2>Case Reports</h2>
                <p>Loading reports...</p>
            </div>
        );
    }

    return (
        <div className="container typography" style={{ padding: '20px' }}>
            <h2>Case Reports</h2>

            {error && (
                <div style={{ color: 'red', padding: '10px', marginBottom: '10px' }}>
                    {error}
                </div>
            )}

            {reports.length === 0 && !error ? (
                <p>No reports available.</p>
            ) : (
                <>
                    <SearchInput
                        data={reports}
                        setFilteredData={setFilteredReports}
                        filterProperties={searchProperties}
                    />
                    <div style={{ marginTop: '10px' }}>
                        <label>
                            <input
                                type="checkbox"
                                checked={hideDownloaded}
                                onChange={e => setHideDownloaded(e.target.checked)}
                            />
                            {' '}Hide Downloaded
                        </label>
                    </div>
                    <table className="table output-report-table" style={{ width: '100%', marginTop: '10px' }}>
                        <thead>
                            <tr>
                                <th>Client Name</th>
                                <th>Patient Name</th>
                                <th>Case ID</th>
                                <th>Report Type</th>
                                <th>Date Published</th>
                                <th>Download</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReports
                                .filter(report => !hideDownloaded || !report.Downloaded)
                                .map(report => (
                                    <OutputReportRow
                                        key={report.PkOutputReportTransferId}
                                        report={report}
                                        api={api}
                                        onDownloaded={handleDownloaded}
                                    />
                                ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default OutputReportList;
